import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";

export default function CartDrawer() {
  const { items, remove, setQty, subtotal, shipping, total, isOpen, setIsOpen } = useCart();

  return (
    <>
      <div
        className={`fixed inset-0 z-[60] bg-[#1B3B36]/60 backdrop-blur-sm transition-opacity ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 bottom-0 w-full max-w-md z-[70] bg-[#F9F8F6] border-l border-[#D8D4CC] transform transition-transform duration-500 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="flex items-center justify-between px-6 py-5 border-b border-[#D8D4CC]">
          <div>
            <div className="overline">Your basket</div>
            <div className="font-serif text-2xl text-[#1B3B36] mt-1">{items.length} {items.length === 1 ? "piece" : "pieces"}</div>
          </div>
          <button
            data-testid="cart-drawer-close"
            onClick={() => setIsOpen(false)}
            className="w-9 h-9 border border-[#1B3B36] text-[#1B3B36] flex items-center justify-center"
            aria-label="Close cart"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6" style={{ height: "calc(100vh - 240px)" }}>
          {items.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag size={28} className="mx-auto text-[#84968C]" />
              <div className="mt-4 font-serif text-2xl text-[#1B3B36]">Your basket is empty.</div>
              <p className="mt-3 text-[13px] text-[#4A4A4A] font-light">Add a piece from the collection to begin.</p>
              <Link
                to="/collections"
                onClick={() => setIsOpen(false)}
                className="mt-6 inline-flex bg-[#1B3B36] text-[#F9F8F6] px-6 py-3 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26]"
              >
                Explore
              </Link>
            </div>
          ) : (
            items.map((i) => (
              <div key={i.key} data-testid={`cart-item-${i.key}`} className="flex gap-4">
                <img src={i.image} alt={i.name} className="w-20 h-24 object-cover bg-[#EFECE5]" />
                <div className="flex-1 flex flex-col">
                  <div className="font-serif text-base text-[#1B3B36] leading-tight">{i.name}</div>
                  {i.size && <div className="mt-1 text-[12px] text-[#4A4A4A]" data-testid={`cart-item-size-${i.key}`}>Size: {i.size} ft</div>}
                  <div className="mt-1 text-[12px] text-[#4A4A4A]">
                    ₹{i.price.toLocaleString("en-IN")}
                  </div>
                  <div className="mt-auto flex items-center justify-between">
                    <div className="flex items-center border border-[#D8D4CC]">
                      <button onClick={() => setQty(i.key, i.quantity - 1)} data-testid={`cart-decrement-${i.key}`} className="w-8 h-8 flex items-center justify-center text-[#1B3B36] hover:bg-[#EFECE5]"><Minus size={12} /></button>
                      <div className="w-8 text-center text-[13px]">{i.quantity}</div>
                      <button onClick={() => setQty(i.key, i.quantity + 1)} data-testid={`cart-increment-${i.key}`} className="w-8 h-8 flex items-center justify-center text-[#1B3B36] hover:bg-[#EFECE5]"><Plus size={12} /></button>
                    </div>
                    <button onClick={() => remove(i.key)} data-testid={`cart-remove-${i.key}`} className="text-[11px] uppercase tracking-[0.18em] text-[#84968C] hover:text-[#B84A3E] link-underline">Remove</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <footer className="border-t border-[#D8D4CC] px-6 py-5 space-y-3">
            <div className="flex justify-between text-[13px] text-[#4A4A4A]">
              <span>Subtotal</span>
              <span>₹{subtotal.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between text-[13px] text-[#4A4A4A]">
              <span>Shipping {subtotal >= 1000 ? "" : "(free above ₹1,000)"}</span>
              <span>{shipping === 0 ? "Free" : `₹${shipping}`}</span>
            </div>
            <div className="flex justify-between font-serif text-xl text-[#1B3B36] pt-3 border-t border-[#D8D4CC]">
              <span>Total</span>
              <span>₹{total.toLocaleString("en-IN")}</span>
            </div>
            <Link
              to="/checkout"
              data-testid="cart-checkout-cta"
              onClick={() => setIsOpen(false)}
              className="mt-2 w-full inline-flex items-center justify-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26]"
            >
              Checkout
            </Link>
          </footer>
        )}
      </aside>
    </>
  );
}
